import React from "react";
import { cn } from "@/lib/utils";
import type { LucideIcon } from "lucide-react";

// ────────────────────────────────────────────────────
// PillTabs — reusable pill/chip-style tab row
// ────────────────────────────────────────────────────

export interface PillTab {
  /** Unique value used to identify the active tab */
  value: string;
  /** Display label */
  label: string;
  /** Optional Lucide icon shown before the label */
  icon?: LucideIcon;
  /** Optional count badge shown after the label */
  count?: number;
  /** Optional per-tab active color override (e.g. "blue", "purple", "emerald") */
  color?: string;
}

/** Color preset mapping for active state classes (solid variant) */
const COLOR_PRESETS: Record<string, { bg: string; text: string; countBg: string; countText: string }> = {
  blue:    { bg: "bg-blue-600",    text: "text-white", countBg: "bg-white/25", countText: "text-white" },
  purple:  { bg: "bg-purple-600",  text: "text-white", countBg: "bg-white/25", countText: "text-white" },
  emerald: { bg: "bg-emerald-600", text: "text-white", countBg: "bg-white/25", countText: "text-white" },
  amber:   { bg: "bg-amber-500",   text: "text-white", countBg: "bg-white/25", countText: "text-white" },
  red:     { bg: "bg-red-600",     text: "text-white", countBg: "bg-white/25", countText: "text-white" },
  indigo:  { bg: "bg-indigo-600",  text: "text-white", countBg: "bg-white/25", countText: "text-white" },
  slate:   { bg: "bg-slate-800",   text: "text-white", countBg: "bg-white/25", countText: "text-white" },
};

export type PillTabsVariant = "soft" | "solid";

export interface PillTabsProps {
  /** Array of tab definitions */
  tabs: PillTab[];
  /** Currently active tab value */
  value: string;
  /** Called when the user clicks a tab */
  onChange: (value: string) => void;
  /**
   * Visual variant:
   * - "soft"  → white card on slate bg (default, clean modern look)
   * - "solid" → filled color pill on active
   */
  variant?: PillTabsVariant;
  /** Tab size */
  size?: "sm" | "md";
  /** Additional className on the container */
  className?: string;
  /** Whether to wrap in the standard scrollable container */
  wrapped?: boolean;
}

export function PillTabs({
  tabs,
  value,
  onChange,
  variant = "soft",
  size = "md",
  className,
  wrapped = true,
}: PillTabsProps) {
  const sizeClasses = size === "sm"
    ? "px-3 py-1 text-xs gap-1.5"
    : "px-4 py-2 text-sm gap-2";

  const iconSize = size === "sm" ? "w-3.5 h-3.5 shrink-0" : "w-4 h-4 shrink-0";

  const pills = (
    <div
      className={cn(
        "inline-flex md:flex w-max md:w-full flex-nowrap md:flex-wrap items-center justify-start h-auto gap-1",
        className,
      )}
    >
      {tabs.map((tab) => {
        const isActive = value === tab.value;
        const Icon = tab.icon;
        const color = tab.color;

        // ── Active state ─────────────────────────────────────────
        let activeClasses = "";
        let countActiveClasses = "bg-white/25 text-white";

        if (isActive) {
          if (color && COLOR_PRESETS[color]) {
            const p = COLOR_PRESETS[color];
            activeClasses = cn(p.bg, p.text, "shadow-sm");
          } else {
            // default: solid blue pill
            activeClasses = "bg-blue-600 text-white shadow-sm";
          }
        }

        // ── Inactive state ────────────────────────────────────────
        const inactiveClasses = "text-slate-600 hover:text-slate-900 hover:bg-slate-100";

        return (
          <button
            key={tab.value}
            type="button"
            onClick={() => onChange(tab.value)}
            className={cn(
              "inline-flex items-center rounded-lg font-medium transition-all duration-150 whitespace-nowrap",
              sizeClasses,
              isActive ? activeClasses : inactiveClasses,
            )}
          >
            {Icon && (
              <Icon
                className={cn(
                  iconSize,
                  isActive ? "opacity-90" : "text-slate-400",
                )}
              />
            )}
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span
                className={cn(
                  "ml-0.5 min-w-[18px] px-1.5 py-0.5 rounded-full text-xs font-semibold leading-none",
                  isActive
                    ? countActiveClasses
                    : "bg-slate-200 text-slate-500",
                )}
              >
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );

  if (!wrapped) return pills;

  return (
    <div className="w-full overflow-x-auto scrollbar-hide">
      <div className="min-w-full w-max bg-white rounded-xl shadow-sm border border-slate-200 p-1">
        {pills}
      </div>
    </div>
  );
}

export default PillTabs;
